import { Cart } from '@diamond/shared/types';
import { useCartStore } from '@diamond/sol-buyer-context';

/**
 * Validates and prepares cart items for selection.
 *
 * Selection rules:
 * - Item must exist in cart
 * - Product stock must be > 0
 * - Use provided quantity if supplied, otherwise use item's current quantity
 *
 * @param itemIds - IDs to select
 * @param cartItemsList - Available items from cache
 * @param quantityMap - Optional quantities to override defaults
 * @returns Array of items ready to add to store (type-safe Cart items)
 */
export function selectCartItems(
  itemIds: string[],
  cartItemsList: Cart[],
  quantityMap?: Map<string, number>
): Cart[] {
  const cartStoreState = useCartStore.getState();

  return itemIds
    .map((itemId) => {
      const item = cartItemsList.find((i) => i.id === itemId);
      if (!item || item.product.stock === 0) {
        return null;
      }

      const quantityToUse =
        quantityMap?.get(itemId) ??
        cartStoreState.cartRecord[itemId]?.quantity ??
        item.quantity ??
        item.product.minimum_order_quantity ??
        1000;

      const validQuantity =
        typeof quantityToUse === 'number' && quantityToUse > 0
          ? quantityToUse
          : item.product.minimum_order_quantity ?? 1000;

      return {
        ...item,
        quantity: validQuantity,
      };
    })
    .filter((item): item is Cart => item !== null);
}

/**
 * Returns item IDs for deselection.
 *
 * Deselection rules:
 * - All provided IDs are marked for removal (no validation needed)
 * - Store handles actual removal
 *
 * @param itemIds - IDs to deselect
 * @returns Same IDs (no transformation needed, store removes)
 */
export function deselectCartItems(itemIds: string[]): string[] {
  return itemIds;
}

/**
 * Validates if an item can be selected.
 *
 * @param itemId - Item to validate
 * @param cartItemsList - Available items
 * @returns true if item exists and has stock
 */
export function canSelectItem(itemId: string, cartItemsList: Cart[]): boolean {
  const item = cartItemsList.find((i) => i.id === itemId);
  return !!item && item.product.stock !== 0;
}
